import { Component, Input, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { AuthService } from '../../auth/auth.service';
import { BoardHelpService } from '../../match-tracker/tracker-board/board-help.service';
import { Match } from '../../_shared/models/match.model';
import { User } from '../../_shared/models/user.model';
import { DisciplineEnum } from '../../_shared/models/csTypes/enums/discipline';
import { environment } from '../../../environments/environment';
import { SubscriptionPermissionService } from '../../_shared/services/subscription-permission.service';
import { TranslateService } from '@ngx-translate/core';

interface Item {
  name: string;
  icon: string;
  lines?: string;
  color: string;
  action: string;
  show: boolean;
  helper?: string;
  disabled?: boolean;
  warning?: string;
}

@Component({
  standalone: false,
  selector: 'app-match-options',
  templateUrl: './match-options.component.html',
  styleUrls: ['./match-options.component.scss'],
})
export class MatchOptionsComponent implements OnInit {
  private modalController = inject(ModalController);
  public router = inject(Router);
  private bhs = inject(BoardHelpService);
  public authService = inject(AuthService);
  private subscriptionPermissionService = inject(SubscriptionPermissionService);
  private translateService = inject(TranslateService);


  @Input() match: Match;
  @Input() isOwner = false;
  @Input() isPointByPoint = false;

  items: Item[];
  trackingOptions: Item[];

  participantSide1: string;
  participantSide2: string;

  async ngOnInit() {
    this.participantSide1 = this.bhs.getPlayersNames(this.match, 1, 'full');
    this.participantSide2 = this.bhs.getPlayersNames(this.match, 2, 'full');

    this.trackingOptions = [
      {
        name: this.translateService.instant('Advanced Tracking'),
        icon: 'stats-chart',
        color: 'secondary',
        action: 'advanced-tracking',
        show:
          !this.router.url.includes('live-scoring') &&
          this.match.discipline !== 'PICKLEBALL',
        helper:
          this.translateService.instant('Get more detailed stats such as aces, double faults, winners, forced and unforced errors, rally length etc.'),
        disabled: false,
      },
      {
        name: this.translateService.instant('Chair Official'),
        icon: 'reader',
        color: 'secondary',
        action: 'official-view',
        show: !this.isOfficial(),
        warning: this.isChairOfficialDisalbed()
          ? this.translateService.instant("Participants must be added to use chair official's mode.")
          : null,
        helper:
          this.translateService.instant('A view designed for chair officials that includes stopwatch, simple stats, code violations, ball changes, select chair sides etc.'),
        disabled: this.isChairOfficialDisalbed(),
      },
    ];
    
    if (this.authService.authUserSignal()?.role?.value >= 200) {
        this.trackingOptions = [...this.trackingOptions, {
            name: this.translateService.instant('Intennse Official'),
            icon: 'reader',
            color: 'warning',
            action: 'temp-intennse',
            show: !this.isIntennseOfficial(),
            warning: this.isIntennseOfficialDisalbed()
              ? this.translateService.instant("Participants must be added to use intennse official view's mode.")
              : null,
            helper: this.translateService.instant('A view designed for Intennse official'),
            disabled: this.isIntennseOfficialDisalbed(),
          }]
     }

    this.items = [
      {
        name: this.translateService.instant('Remove from live score'),
        icon: 'remove-circle',
        color: 'danger',
        action: 'remove-from-live-score',
        show:
          this.match.matchStatus === 'TBP' &&
          this.match.matchUpStatusCodes?.some(
            (matchUpStatusCode) => matchUpStatusCode === 'LIVE_SCORE'
          ),
      },
      {
        name: this.translateService.instant('Swipe teams'),
        icon: 'swap-horizontal',
        color: 'secondary',
        action: 'swip-teams',
        show: this.isIntennseOfficial() && (this.match.matchStatus === 'IN_PROGRESS' || this.match?.matchStatus === 'TBP'),
      },
      {
        name: this.translateService.instant('Edit'),
        icon: 'build',
        color: 'secondary',
        action: 'edit',
        show: true,
        helper: this.translateService.instant('Update players, score format'),
      },
      {
        name: this.translateService.instant('Additional player information'),
        icon: 'information-circle',
        color: 'secondary',
        action: 'additional-information',
        show: Boolean(this.match.tournament),
        helper:
          this.translateService.instant('Information that will show next to the players name such as seed or team'),
      },
      {
        name: this.translateService.instant('Manage clocks'),
        icon: 'timer',
        color: 'secondary',
        action: 'manage-clocks',
        show: true,
        helper: this.translateService.instant('Set your preferences how stopwatch and timer should be used'),
      },
      {
        name: this.translateService.instant('Manage Substitution'),
        icon: 'sync',
        color: 'secondary',
        action: 'manage-substitution',
        show: this.isTeamMatches(),
        helper: this.translateService.instant('Set your preferences how substitution should be used'),
      },
      {
        name: this.translateService.instant('Break Clock'),
        icon: 'time-outline',
        color: 'secondary',
        action: 'break-clock',
        show: this.isIntennseOfficial(),
      },
      {
        name: this.translateService.instant("Subscribe to Score Updates"),
        icon: 'share',
        color: 'secondary',
        action: 'subscribe-to-score-updates',
        show: false,
        helper: this.translateService.instant('Subscribe to receive match score updates'),
      },
      {
        name: this.translateService.instant('Scorekeepers'),
        icon: 'person-add',
        color: 'secondary',
        action: 'add-tracker',
        show: !this.isOfficial(),
        helper: this.translateService.instant('Assign multiple score keepers'),
      },
      {
        name: this.translateService.instant('Officials'),
        icon: 'person-add',
        color: 'secondary',
        action: 'add-official',
        show: Boolean(this.match.tournament),
        helper: this.translateService.instant('Assign officials to the match'),
      },
      {
        name: this.translateService.instant('Assign court'),
        icon: 'add',
        color: 'secondary',
        action: 'assign-court',
        show: true,
        helper: this.translateService.instant('Display court number'),
      },
      {
        name: this.translateService.instant('Assign smart court'),
        icon: 'tv',
        color: 'secondary',
        action: 'assign-smart-court',
        show: Boolean(this.authService.authUserSignal()?.role?.value > 199),
        helper:
          this.translateService.instant('Assign court to a match and send scores to video stream, TV or custom scoreboard'),
      },
      {
        name: this.translateService.instant('Manage camera & scoreboard'),
        icon: 'videocam',
        color: 'secondary',
        action: 'camera',
        show: true,
        helper:
          this.translateService.instant('Add layover to video streaming provider or connect to a scoreboard'),
      },
      {
        name: this.translateService.instant('Change server'),
        icon: 'shuffle',
        color: 'secondary',
        action: 'change-server',
        show: this.hasMatchStarted(),
        helper: this.translateService.instant("Individual, team or don't track server"),
      },
      {
        name: this.translateService.instant('Change returner'),
        icon: 'shuffle',
        color: 'secondary',
        action: 'change-returner',
        show: this.bhs.getLastSet(this.match)?.returnerCourtSides?.length > 0,
        helper: this.translateService.instant('Change returner in doubles'),
      },
      {
        name: this.translateService.instant('Stop match'),
        icon: 'stop-circle',
        color: 'secondary',
        action: 'stop-match',
        show: this.hasMatchStarted(),
        helper: this.translateService.instant('Retire, incomplete, abandoned, default, suspended match'),
      },
      {
        name: this.translateService.instant('Walkover'),
        icon: 'stop-circle',
        color: 'secondary',
        action: 'walkover',
        show: this.isToBePlayed(),
        helper: this.translateService.instant('Award the win before the match starts'),
      },
      {
        name: this.translateService.instant('Tracking mode'),
        icon: 'code-working',
        color: 'secondary',
        action: 'tracking-mode',
        show: !this.isOfficial(),
        helper: this.translateService.instant('Ralley length, serve location'),
      },
      {
        name: this.translateService.instant('Score emit delay'),
        icon: 'alarm',
        color: 'secondary',
        action: 'score-delay',
        show: true,
        helper: this.translateService.instant('Set delay before device releases the score update'),
      },
      {
        name: this.translateService.instant('Weather'),
        icon: 'sunny',
        color: 'secondary',
        action: 'weather',
        show: !this.isOfficial(),
        helper: this.translateService.instant('Temperature, wind, humidity'),
      },
      {
        name: this.isPointByPoint
          ? this.translateService.instant('Allow game by game')
          : this.translateService.instant('Disable game by game'),
        icon: 'tennisball',
        color: this.isPointByPoint ? 'gray' : 'secondary',
        action: 'point-by-point',
        show:
          this.isOfficial() &&
          this.match.discipline !== DisciplineEnum.PICKLEBALL,
        helper:
          this.translateService.instant('Turn on to allow game by game mode. Add games by clicking on game score box'),
      },
      {
        name: this.translateService.instant('Delete'),
        icon: 'trash',
        color: 'danger',
        action: 'delete',
        lines: 'none',
        show: !(this.isPartnerMatch() && this.match.tie),
      },      
    ];
    // this allow to load list once the modal open then check the permission
    this.subscriptionPermissionService.isUserAllowToSubscribeToScore().then(isAllow => {
      if (isAllow) {
        this.items.find(item => item.action === 'subscribe-to-score-updates')!.show = isAllow;
      }
    });
  }

  dismiss(action = null) {
    this.modalController.dismiss(action);
  }

  hasMatchStarted(): boolean {
    return this.match.matchStatus === 'IN_PROGRESS' && this.match.discipline !== DisciplineEnum.PICKLEBALL;
  }

  isToBePlayed(): boolean {
    return this.match.matchStatus === 'TBP';
  }

  isOfficial(): boolean {
    return this.router.url.includes('official-view');
  }

  isIntennseOfficial(): boolean {
    return this.router.url.includes('intennse-official-view');
  }

  isTeamMatches(): boolean {
    return (this.router.url.includes('ties') || this.router.url.includes('intennse-official-view')) && !!this.match.tie ;
  }

  private isPartnerMatch(): boolean {
    return this.match.organisations?.length > 0;
  }

  isChairOfficialDisalbed(): boolean {
    return !this.match.sides.every((s) =>
      s.players.every((p) => p.participant && Object.keys(p.participant).length > 0)
    );
  }

  isIntennseOfficialDisalbed(): boolean {
    return !this.match.sides.every((s) =>
      s.players.every((p) => p.participant && Object.keys(p.participant).length > 0)
    );
  }

}
